
import { defineComponent, PropType } from 'vue'

import { RunDetailMutations } from '~/store/run/detail'
import { CheckIssue, IgnoreCheckIssueActionChoice } from '~/types/types'
import { UiIssueLevel } from '~/utils/issue'
import { SortFilter } from './sortFilter'

const VISIBLE_PAGES = 5

export default defineComponent({
  name: 'IssueList',
  props: {
    totalCount: Number,
    pageSize: Number,
    startPage: Number,
    searchValue: {
      type: [String, Number, Boolean] as PropType<string | number | boolean | null>,
      default: ''
    },
    blobUrlRoot: String,
    checkId: String,
    canIgnoreIssues: Boolean,
    issueOccurrences: {
      type: Array as PropType<CheckIssue[]>,
      required: true
    },
    issueIndex: Number,
    snippetsLoading: {
      type: Boolean,
      default: false
    },
    snippetsFetchErrored: {
      type: Boolean,
      default: false
    },
    isSubrepo: {
      type: Boolean,
      default: false
    },
    repoPath: {
      type: String,
      default: ''
    },
    disableFp: {
      type: Boolean,
      default: false
    },
    issueLevel: {
      type: String as PropType<UiIssueLevel>,
      default: UiIssueLevel.Check
    }
  },

  data() {
    return {
      issuesIgnored: [] as string[],
      IgnoreCheckIssueActionChoice,
      sortFilters: [
        { label: 'First Seen', icon: 'first-seen', name: 'first-seen' },
        { label: 'Last Seen', icon: 'last-seen', name: 'last-seen' }
      ] as SortFilter[]
    }
  },

  mounted() {
    this.loadIgnoredIssues()
    this.$root.$on('update-ignored-issues-checks', this.loadIgnoredIssues)
  },
  beforeDestroy() {
    this.$root.$off('update-ignored-issues-checks', this.loadIgnoredIssues)
  },

  methods: {
    loadIgnoredIssues(): void {
      this.issuesIgnored = (this.$localStore.get('check-issues', this.localKey) as string[]) ?? []
    },

    ignoreIssues(issueIds: string[]): void {
      this.issuesIgnored = [...new Set(this.issuesIgnored.concat(issueIds))]
      this.$localStore.set('check-issues', this.localKey, this.issuesIgnored)

      const { analyzer, runId, issueId } = this.$route.params

      const newPageRefetchStatus = {
        issueOccurrences: {
          status: true,
          issueId, // Specify the `issueId` so that the re-fetch happens on visiting the issue occurrences page with a matching `issueId`
          page: this.$route.query.page ? Number(this.$route.query.page) : 1
        },
        runs: { status: true },
        runDetail: {
          status: true,
          analyzer,
          runId,
          pageOffset: Math.floor(this.issueIndex ? this.issueIndex / 10 : 0) * 10 // Specify the page offset so that the re-fetch happens on visiting the page the issue resided
        }
      }

      this.$store.commit(
        `run/detail/${RunDetailMutations.SET_PAGE_REFETCH_STATUS}`,
        newPageRefetchStatus
      )
    },

    debounceSearch(value: string): void {
      this.$emit('search', value)
    },

    updateSort(value: string): void {
      this.$emit('sort', value)
    },

    updatePage(page: number): void {
      this.$emit('page', page)
    }
  },
  computed: {
    pageCount(): number {
      return Math.ceil(this.totalCount && this.pageSize ? this.totalCount / this.pageSize : 0)
    },

    totalVisible(): number {
      return this.pageCount >= VISIBLE_PAGES ? VISIBLE_PAGES : this.pageCount
    },

    emptyStateTitle() {
      return this.searchValue
        ? `No results found for '${this.searchValue}'`
        : 'No more occurrences of this issue'
    },

    emptyStateSubtitle() {
      return this.searchValue ? 'Please try changing your search query' : 'Give yourself a cookie.'
    },

    emptyStateImagePaths() {
      return {
        png: this.searchValue
          ? require('~/assets/images/ui-states/directory/empty-search.gif')
          : require('~/assets/images/ui-states/issues/no-issues-found-static-140px.png'),
        webp: this.searchValue
          ? require('~/assets/images/ui-states/directory/empty-search.webp')
          : require('~/assets/images/ui-states/issues/no-issues-found-static-140px.webp')
      }
    },

    localKey(): string {
      const { owner, repo, issueId } = this.$route.params
      return `${owner}-${repo}-${this.checkId}-${issueId}-ignored-issues`
    }
  }
})
